'use client';

import React, { createContext, useState, useContext, ReactNode } from 'react';

export type BreadcrumbItem = {
    label: string;
    href?: string;
    isCurrentPage?: boolean;
};

type BreadcrumbContextType = {
    breadcrumbs: BreadcrumbItem[];
    setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextType>({
    breadcrumbs: [],
    setBreadcrumbs: () => {}
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

    return (
        <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
            {children}
        </BreadcrumbContext.Provider>
    );
}

export function useBreadcrumb() {
    return useContext(BreadcrumbContext);
}

export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
    const { setBreadcrumbs } = useBreadcrumb();

    // Update the breadcrumbs when the component mounts or items change
    React.useEffect(() => {
        setBreadcrumbs(items);

        // Reset to empty when unmounting
        return () => setBreadcrumbs([]);
    }, [items, setBreadcrumbs]);

    // This component doesn't render anything
    return null;
}

// Utility function to generate breadcrumbs from path segments
export function generateBreadcrumbsFromPath(
    pathname: string,
    pathLabels?: Record<string, string>
): BreadcrumbItem[] {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always add home
    breadcrumbs.push({
        label: 'Home',
        href: '/'
    });

    // Add each segment
    let currentPath = '';
    segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === segments.length - 1;

        breadcrumbs.push({
            label:
                pathLabels?.[segment] ||
                segment.charAt(0).toUpperCase() + segment.slice(1),
            href: isLast ? undefined : currentPath,
            isCurrentPage: isLast
        });
    });

    return breadcrumbs;
}

// Component to automatically generate breadcrumbs from current path
export function AutoBreadcrumbs({
    pathLabels,
    customItems
}: {
    pathLabels?: Record<string, string>;
    customItems?: BreadcrumbItem[];
}) {
    const [pathname, setPathname] = React.useState('');

    React.useEffect(() => {
        // Get current pathname on client side
        setPathname(window.location.pathname);
    }, []);

    const items =
        customItems ||
        (pathname ? generateBreadcrumbsFromPath(pathname, pathLabels) : []);

    return <Breadcrumbs items={items} />;
}
